import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@outpave/ds";
import { GET_RECEIPT_PROCESSING_STATUS } from "graphql/receipts";

export function useReceiptExtractionProgress(
  transactionId: string | null | undefined,
  onChanged: () => unknown | Promise<unknown>,
  onProgress?: (receiptId: string, status: string) => void,
) {
  const client = useApolloClient();
  const { t } = useTranslation();
  const [pending, setPending] = useState<string[]>([]);
  const latest = useRef({ transactionId, onChanged, onProgress, t });
  latest.current = { transactionId, onChanged, onProgress, t };

  useEffect(() => {
    setPending([]);
  }, [transactionId]);

  useEffect(() => {
    if (!pending.length) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const refresh = async () => {
      const finished = new Set<string>();
      for (const id of pending) {
        try {
          const { data } = await client.query({
            query: GET_RECEIPT_PROCESSING_STATUS,
            variables: { id },
            fetchPolicy: "network-only",
          });
          if (cancelled) return;
          const status = data?.receiptDetail?.processingStatus;
          if (status !== "COMPLETE" && status !== "FAILED") {
            if (status) latest.current.onProgress?.(id, status);
            continue;
          }
          await latest.current.onChanged();
          if (cancelled) return;
          if (status === "FAILED") toast.error(latest.current.t("Scan Failed"));
          latest.current.onProgress?.(id, status);
          finished.add(id);
        } catch (error) {
          if (!cancelled)
            console.error("Unable to refresh receipt processing:", error);
        }
      }
      if (!cancelled) {
        // Removing one upload mid-loop restarts the effect and cancels the
        // status update for the next upload in the same polling cycle.
        if (finished.size)
          setPending((ids) => ids.filter((id) => !finished.has(id)));
        timer = setTimeout(refresh, 10000);
      }
    };

    timer = setTimeout(refresh, 10000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, pending, transactionId]);

  return useCallback(
    (receiptId: string) => {
      // Ignore an upload response belonging to a drawer that has switched transactions.
      if (latest.current.transactionId !== transactionId) return;
      setPending((ids) =>
        ids.includes(receiptId) ? ids : [...ids, receiptId],
      );
    },
    [transactionId],
  );
}
